import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import './index.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import Dashboard from './Dashboard';
import TenantLandingPage from "./TenantLandingPage.js";
import AdminPage from "./AdminPage.js";
import CompanyPage from "./CompanyPage.js";
import CompanyDashboard from "./components/CompanyDashboard/index.js";
import CustomSourcePage from './CustomSourcePage';
import CompanyAnalysisTable from './CompanyAnalysisTable'
import FilteredSummaryTableNew from './FilteredSummaryTableNew.js'
import QuestionsPage from "./QuestionsPage";
import SettingsPage from './SettingsPage';
import RequestStatus from './RequestStatus.js'
import UploadCompanyFiles from './UploadCompanyFiles.js'
import PaymentPlatformAssessmentsPage from './PaymentPlatformAssessmentsPage.js'
import PaymentPlatformCompanyReport from './PaymentPlatformCompanyReport.js'
import OfferLiftBorrowerPage from './OfferLiftBorrowerPage.js';
import TenantDashboardHome from './TenantDashboardHome.js'
import BIReporting from './BIReporting.js'
import Insurance from './Insurance.js'
import DistributorPortal from './DistributorPortal.js'
import EmbedWidget from './EmbedWidget.js'
import BillingPage from './BillingPage.js'
import PlatformConsole from './PlatformConsole.js'
import ApiDocs from './ApiDocs.js'
import TenantDetail from './TenantDetail.js'
import FinancialAnalysisPage from './FinancialAnalysisPage';
import SyntheticTrainingPage from './SyntheticTrainingPage';
import DistributorDetail from './DistributorDetail.js'
import DistributorClientPage from './DistributorClientPage.js'
import PortfolioRiskWorkspace from './portfolio-risk';
import CurrencyExposureWorkspace from './currency-risk';
import { isCurrencyRiskEnabled } from './config/tenantRollouts';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useParams,
} from "react-router-dom";
import { createTheme, ThemeProvider, CssBaseline, ScopedCssBaseline } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import 'react-toastify/dist/ReactToastify.css';
import './toastify-overrides.css';
import { Provider, useSelector } from 'react-redux';
import { store } from "./app/store.js";
import { SearchQueryProvider } from '../src/context/searchContext.js';
import { AddCompanyProvider } from '../src/context/addCompanyModalContext.js';
import { ProspectsARProvider } from './context/prospectsARContext.js';
import { ARSettingsProvider } from './context/arSettingsContext.js';
import { FilterProvider } from './context/filterContext.js';
import { FeatureFlagProvider } from './context/featureFlagContext.js';
import { UserPreferencesProvider } from './context/userPreferencesContext.js';
import { SegmentProvider } from './context/segmentContext.js';
import { RecentlyAddedCompaniesProvider } from './context/recentlyAddedCompaniesContext.js';
import { normalizeRowData } from './utils/Helper.js';
import underwritingApi from './api/underwritingApi';
import { TokenReadyProvider, useTokenReady } from './TokenReadyProvider';
import { RolePermissionsProvider } from './context/RolePermissionsContext';
import { LicenseInfo } from '@mui/x-license-pro';
import { StatusProvider } from './context/statusContext.js';
import { resolveAuthConfig } from './config/auth.js';
import BrandThinkingLoader from './components/BrandThinkingLoader.js';
LicenseInfo.setLicenseKey('4b741527e94a9f1cc1378d5db2a7df3dTz04MDgwNyxFPTE3MzQ2MDU0NTkwMDAsUz1wcmVtaXVtLExNPXN1YnNjcmlwdGlvbixLVj0y');


// Public Auth0 defaults are bundled so a deployment cannot silently produce an
// `undefined/authorize` URL when its build environment omits CRA variables.
const authConfig = resolveAuthConfig();

const onAuthRedirect = (appState) => {
  const returnTo = appState?.returnTo;
  window.history.replaceState({}, document.title, returnTo && returnTo.startsWith('/') ? returnTo : '/');
};

const customTheme = createTheme({
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width: 1200px)': {
            maxWidth: 'none',
          },
        },
      },
    },
  },
});

// TokenGate component to ensure Dashboard only renders when token is ready
const TokenGate = ({ children }) => {
  const tokenReady = useTokenReady();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();

  if (authLoading) {
    return <BrandThinkingLoader fullScreen />;
  }

  if (!isAuthenticated) {
    return <BrandThinkingLoader fullScreen />;
  }

  if (!tokenReady) {
    return <BrandThinkingLoader fullScreen />;
  }

  return children;
};

const JFrogCurrencyExposure = () => {
  const { tenantIdp } = useParams();
  const currentTenant = useSelector((state) => state.userInfo.tenantId);
  const tenantId = tenantIdp || currentTenant;
  const fallback = tenantIdp ? `/tenant-dashboard/${tenantIdp}` : "/";
  return isCurrencyRiskEnabled(tenantId)
    ? <CurrencyExposureWorkspace />
    : <Navigate to={fallback} replace />;
};

const routerConfig = [
  {
    // Standalone real-app training route: avoid normal Dashboard data/providers
    // and customer mutation controls. Server eligibility is checked in the page.
    path: '/tenant-dashboard/:tenantIdp/synthetic-training',
    element: <TokenGate><SyntheticTrainingPage /></TokenGate>,
  },
  {
    // Public, tokenized borrower interview — deliberately outside TokenGate.
    path: "/offerlift/:token",
    element: <OfferLiftBorrowerPage />,
  },
  {
    // Public, auth-less embeddable BI widget (tokenized) — sits outside TokenGate.
    path: "/embed/:token",
    element: <EmbedWidget />,
  },
  {
    // Distributor Portal — a standalone authenticated surface (not the tenant Dashboard layout).
    path: "/distributor",
    element: (
      <TokenGate>
        <DistributorPortal />
      </TokenGate>
    ),
  },
  {
    path: "/distributor/client/:clientId",
    element: (
      <TokenGate>
        <DistributorClientPage />
      </TokenGate>
    ),
  },
  {
    path: "/platform/distributor/:did/dashboard/client/:clientId",
    element: (
      <TokenGate>
        <DistributorClientPage adminPreview />
      </TokenGate>
    ),
  },
  {
    path: "/platform/distributor/:did/dashboard",
    element: (
      <TokenGate>
        <DistributorPortal adminPreview />
      </TokenGate>
    ),
  },
  {
    path: "/",
    element: (
      <TokenGate>
        <SegmentProvider>
          <Dashboard />
        </SegmentProvider>
      </TokenGate>
    ),
    children: [
      {
        path: "/",
        element: (<TenantLandingPage />),
      },
      {
        path: "company/:companyId",
        element: <CompanyPage />,
      },
      {
        path: "financial-analysis/:companyId",
        element: <FinancialAnalysisPage />,
      },
      {
        path: "requeststatus",
        element: <RequestStatus />,
      },
      {
        path: "company/:companyId/custom",
        element: <CustomSourcePage />,
      },
      {
        path: "company/:companyId/analysis",
        element: <CompanyAnalysisTable />,
      },
      {
        path: "company",
        element: <CompanyDashboard />,
      },
      {
        path: "questions",
        element: <QuestionsPage />,
      },
      {
        path: "filteredSummary",
        element: <FilteredSummaryTableNew />,
      },
      {
        path: "settings",
        element: <SettingsPage openOthers={false} />,
      },
      {
        path: "settings/AREnableRequest",
        element: <SettingsPage openOthers={true} />,
      },
      {
        path: "requeststatus",
        element: <RequestStatus />,
      },
      {
        path: "uploadcompanyfiles",
        element: <UploadCompanyFiles />,
      },
      {
        path: "assessments",
        element: <PaymentPlatformAssessmentsPage />,
      },
      {
        path: "assessments/company/:entityId",
        element: <PaymentPlatformCompanyReport />,
      },
      {
        path: "reporting",
        element: <BIReporting />,
      },
      {
        path: "portfolio-risk",
        element: <PortfolioRiskWorkspace />,
      },
      {
        path: "currency-risk",
        element: <JFrogCurrencyExposure />,
      },
      {
        path: "insurance",
        element: <Insurance />,
      },
      {
        path: "billing",
        element: <BillingPage />,
      },
      {
        path: "platform",
        element: <PlatformConsole />,
      },
      {
        path: "analytics",
        element: <Navigate to="/platform" replace />,
      },
      {
        path: "api",
        element: <ApiDocs />,
      },
      {
        path: "platform/tenant/:tid",
        element: <TenantDetail />,
      },
      {
        path: "platform/distributor/:did",
        element: <DistributorDetail />,
      }
    ],
  },
  {
    path: "/tenant-dashboard/:tenantIdp",
    element: (
      <TokenGate>
        <SegmentProvider>
          <Dashboard adminAction={true} />
        </SegmentProvider>
      </TokenGate>
    ),
    children: [
      {
        path: "/tenant-dashboard/:tenantIdp",
        element: <TenantDashboardHome />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/company",
        element: <CompanyDashboard adminAction={true} />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/questions",
        element: <QuestionsPage />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/filteredSummary/",
        element: <FilteredSummaryTableNew adminAction={true} />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/company/:companyId/",
        element: <CompanyPage adminAction={true} />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/financial-analysis/:companyId",
        element: <FinancialAnalysisPage />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/company/:companyId/custom",
        element: <CustomSourcePage />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/company/:companyId/analysis/",
        element: <CompanyAnalysisTable />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/requeststatus/",
        element: <RequestStatus adminAction={true} />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/uploadcompanyfiles",
        element: <UploadCompanyFiles adminAction={true} />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/assessments",
        element: <PaymentPlatformAssessmentsPage />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/assessments/company/:entityId",
        element: <PaymentPlatformCompanyReport adminAction />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/settings/",
        element: <SettingsPage adminAction={true} />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/settings/AREnableRequest/",
        element: <SettingsPage adminAction={true} openOthers={true} />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/reporting",
        element: <BIReporting />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/portfolio-risk",
        element: <PortfolioRiskWorkspace />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/currency-risk",
        element: <JFrogCurrencyExposure />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/insurance",
        element: <Insurance />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/billing",
        element: <BillingPage />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/platform",
        element: <PlatformConsole />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/analytics",
        element: <Navigate to="/platform" replace />,
      },
      {
        path: "/tenant-dashboard/:tenantIdp/api",
        element: <ApiDocs />,
      }
    ],
  }
];

const router = createBrowserRouter(routerConfig);

// Wrap the app in ApiClientProvider
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ARSettingsProvider>
      <FeatureFlagProvider>
        <FilterProvider>
          <SearchQueryProvider>
            <AddCompanyProvider>
                <Provider store={store}>
                  <Auth0Provider
                    domain={authConfig.domain}
                    clientId={authConfig.clientId}
                    authorizationParams={{
                      redirect_uri: authConfig.redirectUri,
                      audience: authConfig.audience,
                      scope: "openid profile email offline_access"
                    }}
                    cacheLocation="localstorage"
                    useRefreshTokens={true}
                    useRefreshTokensFallback={true}
                    onRedirectCallback={onAuthRedirect}
                  >
                    <TokenReadyProvider>
                      <RolePermissionsProvider>
                        <UserPreferencesProvider>
                          <ProspectsARProvider>
                            <RecentlyAddedCompaniesProvider>
                              <StatusProvider>
                                <ToastContainer
                                  position='bottom-right'
                                  autoClose={4000}
                                  newestOnTop
                                  closeOnClick
                                  pauseOnHover
                                  draggable
                                  theme='light'
                                />
                                <RouterProvider router={router} />
                              </StatusProvider>
                            </RecentlyAddedCompaniesProvider>
                          </ProspectsARProvider>
                        </UserPreferencesProvider>
                      </RolePermissionsProvider>
                    </TokenReadyProvider>
                  </Auth0Provider>
                </Provider>
            </AddCompanyProvider>
          </SearchQueryProvider>
        </FilterProvider>
      </FeatureFlagProvider>
    </ARSettingsProvider>
  </React.StrictMode>
);
